import type { SendMessagePayload, Message } from '@standardagents/client'

// Global client instance (set by AgentBuilderProvider)
let globalEndpoint: string | null = null

/**
 * Internal function to set the global endpoint.
 * Called by AgentBuilderProvider when it mounts.
 * @internal
 */
export function __setGlobalEndpoint(endpoint: string) {
  globalEndpoint = endpoint
}

/**
 * Send a message to a specific thread.
 *
 * This is a standalone function that sends messages to threads.
 * It requires that an AgentBuilderProvider is mounted somewhere in your app
 * to set the global endpoint configuration.
 *
 * @param id - The thread ID to send the message to
 * @param payload - The message payload containing role, content, and optional silent flag
 * @returns Promise resolving to the created message
 *
 * @throws Error if called before AgentBuilderProvider is mounted
 *
 * @example
 * ```tsx
 * import { sendMessage } from '@standardagents/react'
 *
 * await sendMessage('thread-123', {
 *   role: 'user',
 *   content: 'Hello, agent!',
 * })
 *
 * // Send a silent message
 * await sendMessage('thread-123', {
 *   role: 'user',
 *   content: 'Silent message',
 *   silent: true,
 * })
 * ```
 */
export async function sendMessage(
  id: string,
  payload: SendMessagePayload
): Promise<Message> {
  if (!globalEndpoint) {
    throw new Error(
      'sendMessage requires AgentBuilderProvider to be mounted in your app'
    )
  }

  // Read auth token from localStorage
  const token =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('agentbuilder_auth_token')
      : null

  // Build headers
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  // Make the request
  const response = await fetch(`${globalEndpoint}/threads/${id}/messages`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    throw new Error(`Failed to send message: ${response.statusText}`)
  }

  return response.json()
}
